// Considered: MeasureMembersListMolecule — rejected: it is measure-specific (weights, patch); the Members
// read model, inherited block and undo flow have no counterpart. Follows its in-component graphClient fetch.
// See spec Reuse Audit.
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import type { InheritedMember, InheritedMembers, SphereMemberGroup, SphereMembersPageResponse, SphereMembersResponse } from 'types/sphereMembers';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useSphereInfo } from '../../../hooks/useSphereInfo';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ConfirmDialogAtom from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import { CollapsibleSectionAtom } from '../../atoms/CollapsibleSection';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import MemberGroupMolecule from '../../molecules/MemberGroup/MemberGroupMolecule';
import MemberRowMolecule from '../../molecules/MemberRow/MemberRowMolecule';
import DialogWrapper from '../../molecules/DialogWrapper/DialogWrapper';
import PerspectiveSelector from '../../molecules/PerspectiveSelector/PerspectiveSelector';
import type { SphereSectionSlotContext } from '../SphereEditor/sphereSectionSlots';
import './SphereMembersOrganism.scss';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; reason: 'forbidden' | 'unavailable' }
  | { status: 'ready'; data: SphereMembersResponse };

type GroupPaging = { loading: boolean; failed: boolean };

/** What Undo needs to put a removed holder back exactly as it was. */
type UndoTarget = {
  perspectiveSystemName: string;
  perspectiveLabel: string;
  subjectId: string;
  urgency: number | null;
  failedReason?: string;
};

type GroupAction = { group: SphereMemberGroup; label: string };

/**
 * Where focus goes once the list has re-rendered: a row (after Add or Undo), a row's Remove button
 * (after the self-removal confirm is cancelled), the group (after its row is gone), or the inherited
 * group's header (after its Retry, whose button the refreshed block may no longer render).
 */
type FocusRequest =
  | { target: 'row' | 'remove' | 'group'; perspectiveSystemName: string; subjectId?: string }
  | { target: 'inherited' };

/** "Show all" reads the rest of a group at the route's largest page size. */
const SHOW_ALL_PAGE_SIZE = 100;

/** The inherited group starts collapsed above this many rows; its header always shows the count. */
const INHERITED_EXPANDED_MAX = 10;

const KNOWN_REASONS = new Set(['manage_security_required', 'relation_not_assignable', 'permission_service_unreachable']);

const errorReason = (err: unknown): string => {
  const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return code && KNOWN_REASONS.has(code) ? code : 'other';
};

const useMemberName = (subjectId: string | undefined): string => {
  const { t } = useTranslation();
  const { info } = useSphereInfo(subjectId);
  return info?.properties?.name ?? t('sphere.members.unknownMember');
};

/** The element inside `root` that a focus request names, or null when it is not rendered. */
const findFocusTarget = (root: HTMLElement, request: FocusRequest): HTMLElement | null => {
  if (request.target === 'inherited') {
    return root.querySelector<HTMLElement>('[data-testid="members-inherited"] .collapsible-section__header');
  }
  const group = root.querySelector<HTMLElement>(`[data-testid="member-group-${request.perspectiveSystemName}"]`);
  if (!group) return null;
  const row = request.subjectId
    ? Array.from(group.querySelectorAll<HTMLElement>('[data-subject-id]')).find(
        (el) => el.dataset.subjectId === request.subjectId
      )
    : undefined;
  if (request.target === 'row' && row) return row;
  if (request.target === 'remove' && row) return row.querySelector<HTMLElement>('.member-row__remove');
  return group.querySelector<HTMLElement>('.collapsible-section__header');
};

const UndoNotice: React.FC<{
  target: UndoTarget;
  /** The refreshed list still lets the caller add this person back here. */
  canUndo: boolean;
  /** Whether the caller may add was not answered; the gates notice says so and offers Retry. */
  gatesUnavailable: boolean;
  onUndo: () => void;
  undoing: boolean;
}> = ({ target, canUndo, gatesUnavailable, onUndo, undoing }) => {
  const { t } = useTranslation();
  const name = useMemberName(target.subjectId);
  const containerRef = useRef<HTMLOutputElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    (container?.querySelector('button') ?? container)?.focus();
  }, []);

  let text = t('sphere.members.undo.removed', { name, perspective: target.perspectiveLabel });
  if (!canUndo && !gatesUnavailable) text = t('sphere.members.undo.cannotRestore', { name });

  return (
    <output className="sphere-members__note" ref={containerRef} tabIndex={-1} data-testid="members-undo">
      <span>{text}</span>
      {canUndo ? (
        <ButtonAtom
          type="button"
          variant="text"
          size="small"
          label={t(target.failedReason ? 'common.retry' : 'sphere.members.undo.action')}
          loading={undoing}
          onClick={onUndo}
        />
      ) : null}
      {canUndo && target.failedReason ? (
        <span className="sphere-members__error" role="alert">
          {t('sphere.members.undo.failed', { name, reason: t(`sphere.members.reason.${target.failedReason}`) })}
        </span>
      ) : null}
    </output>
  );
};

/** A single-holder perspective: adding replaces whoever holds it, named when the caller can see them. */
const ReplaceNotice: React.FC<{ group: SphereMemberGroup }> = ({ group }) => {
  const { t } = useTranslation();
  const currentId = group.members[0]?.subjectId;
  const name = useMemberName(currentId);
  if (currentId) {
    return <p className="sphere-members__note">{t('sphere.members.addDialog.replaces', { current: name })}</p>;
  }
  if (group.hiddenCount > 0) {
    return <p className="sphere-members__note">{t('sphere.members.addDialog.replacesHidden')}</p>;
  }
  return null;
};

const InheritedRow: React.FC<{
  member: InheritedMember;
  perspectiveLabels: ReadonlyMap<string, string>;
}> = ({ member, perspectiveLabels }) => {
  const { t } = useTranslation();
  const { info } = useSphereInfo(member.source.sphereId ?? undefined);
  const { perspectiveSystemName, via, sphereId } = member.source;
  const perspective = perspectiveLabels.get(perspectiveSystemName) ?? perspectiveSystemName;
  const sourceName = sphereId ? info?.properties?.name : undefined;
  let sourceText: string;
  if (via === 'center') {
    sourceText = sourceName
      ? t('sphere.members.inherited.sourceCenterNamed', { perspective, sphere: sourceName })
      : t('sphere.members.inherited.sourceCenter', { perspective });
  } else if (sourceName) {
    sourceText = t('sphere.members.inherited.sourcePurpose', { perspective, sphere: sourceName });
  } else {
    sourceText = t('sphere.members.inherited.sourceUnknown', { perspective });
  }
  return (
    <MemberRowMolecule
      member={{ ...member, assignedAt: null, urgency: null, canRemove: false }}
      perspectiveLabel={perspective}
      level={member.level}
      sourceText={sourceText}
    />
  );
};

const OrgWideNotice: React.FC<{ centerId: string; level: string }> = ({ centerId, level }) => {
  const { t } = useTranslation();
  const { info } = useSphereInfo(centerId);
  return (
    <p className="sphere-members__note" data-testid="members-org-wide">
      {t('sphere.members.inherited.orgWide', {
        center: info?.properties?.name ?? t('sphere.members.unknownMember'),
        level: t(`sphere.members.levelVerb.${level}`),
      })}
    </p>
  );
};

/** Rows sharing a source sit together, sources in the order the server ranks them (nearest first). */
const groupBySource = (members: InheritedMember[]): InheritedMember[] => {
  const sourceKey = ({ source }: InheritedMember) => `${source.via}|${source.sphereId ?? ''}|${source.perspectiveSystemName}`;
  const rank = new Map<string, number>();
  members.forEach((member) => {
    const key = sourceKey(member);
    if (!rank.has(key)) rank.set(key, rank.size);
  });
  return [...members].sort((a, b) => (rank.get(sourceKey(a)) ?? 0) - (rank.get(sourceKey(b)) ?? 0));
};

const InheritedBlock: React.FC<{
  inherited: InheritedMembers;
  perspectiveLabels: ReadonlyMap<string, string>;
  onRetry: () => void;
}> = ({ inherited, perspectiveLabels, onRetry }) => {
  const { t } = useTranslation();
  const listed = inherited.status === 'complete' || inherited.status === 'partial';
  const count = listed ? inherited.members.length + inherited.hiddenCount : null;

  let body: React.ReactNode;
  if (inherited.status === 'private') {
    body = <p className="sphere-members__note">{t('sphere.members.inherited.private')}</p>;
  } else if (inherited.status === 'unavailable') {
    body = (
      <output className="sphere-members__note">
        <MessageAtom
          severity="warn"
          text={t(
            inherited.reason === 'permission_checks_bypassed'
              ? 'sphere.members.inherited.bypassed'
              : 'sphere.members.inherited.unavailable'
          )}
        />
        {inherited.reason === 'permission_service_unreachable' ? (
          <ButtonAtom type="button" variant="text" size="small" label={t('common.retry')} onClick={onRetry} />
        ) : null}
      </output>
    );
  } else {
    const { members, hiddenCount, orgWide } = inherited;
    body = (
      <>
        {inherited.status === 'partial' ? (
          <p className="sphere-members__note" data-testid="members-inherited-partial">
            {t('sphere.members.inherited.partial', {
              checked: inherited.coverage.checked,
              candidates: inherited.coverage.candidates,
            })}
          </p>
        ) : null}
        {orgWide ? <OrgWideNotice centerId={orgWide.centerId} level={orgWide.level} /> : null}
        {members.length === 0 && hiddenCount === 0 && !orgWide ? (
          <p className="sphere-members__note">{t('sphere.members.inherited.empty')}</p>
        ) : null}
        {members.length > 0 ? (
          <ul className="sphere-members__inherited-list">
            {groupBySource(members).map((member) => (
              <InheritedRow key={member.subjectId} member={member} perspectiveLabels={perspectiveLabels} />
            ))}
          </ul>
        ) : null}
        {hiddenCount > 0 ? (
          <p className="sphere-members__note">
            {t(members.length > 0 ? 'sphere.members.hiddenCount' : 'sphere.members.allHidden', { count: hiddenCount })}
          </p>
        ) : null}
      </>
    );
  }

  return (
    <CollapsibleSectionAtom
      title={
        count === null ? t('sphere.members.inherited.title') : t('sphere.members.inherited.titleCount', { count })
      }
      defaultExpanded={count === null || count <= INHERITED_EXPANDED_MAX}
      data-testid="members-inherited"
    >
      {body}
    </CollapsibleSectionAtom>
  );
};

const SphereMembersView: React.FC<SphereSectionSlotContext> = ({ sphere, mode }) => {
  const { t } = useTranslation();
  const { t: tSchema } = useTranslation('schema');
  const apiClients = useApiClientsOrNull();
  const sphereId = sphere.id;
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [paging, setPaging] = useState<Record<string, GroupPaging>>({});

  // `silent` re-fetches after a write without replacing the list with skeletons.
  const load = useCallback(
    async (silent = false) => {
      if (!apiClients || !sphereId) return;
      if (!silent) setState({ status: 'loading' });
      try {
        const data = await apiClients.graphClient.get<SphereMembersResponse>(
          `/spheres/${encodeURIComponent(sphereId)}/members`
        );
        setState({ status: 'ready', data });
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        setState({ status: 'error', reason: status === 403 ? 'forbidden' : 'unavailable' });
      }
    },
    [apiClients, sphereId]
  );

  const assignUrl = `/spheres/${encodeURIComponent(sphereId ?? '')}/perspectives/assign-subject`;
  const [addTarget, setAddTarget] = useState<GroupAction | null>(null);
  const pickerLabelId = useId();
  const [selected, setSelected] = useState<{ id: string; name: string } | null>(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [removingIds, setRemovingIds] = useState<ReadonlySet<string>>(new Set());
  const [removeError, setRemoveError] = useState<{ perspective: string; reason: string } | null>(null);
  const [undo, setUndo] = useState<UndoTarget | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [selfRemoval, setSelfRemoval] = useState<(GroupAction & { subjectId: string }) | null>(null);
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Runs after the re-fetch that follows a write has rendered, so the row it names exists (or is gone).
  useEffect(() => {
    const root = rootRef.current;
    if (!focusRequest || !root || state.status !== 'ready') return;
    (findFocusTarget(root, focusRequest) ?? root).focus();
    setFocusRequest(null);
  }, [focusRequest, state]);

  const closeAdd = useCallback(() => {
    setAddTarget(null);
    setSelected(null);
    setAddError(null);
  }, []);

  const submitAdd = useCallback(async () => {
    if (!apiClients || !addTarget || !selected) return;
    setAdding(true);
    setAddError(null);
    const perspectiveSystemName = addTarget.group.perspective.systemName;
    try {
      await apiClients.graphClient.post(assignUrl, { perspectiveSystemName, subjectId: selected.id });
      closeAdd();
      setUndo(null);
      await load(true);
      setFocusRequest({ target: 'row', perspectiveSystemName, subjectId: selected.id });
    } catch (err) {
      setAddError(errorReason(err));
    } finally {
      setAdding(false);
    }
  }, [apiClients, addTarget, selected, assignUrl, closeAdd, load]);

  const performRemove = useCallback(
    async ({ group, label }: GroupAction, subjectId: string, offerUndo: boolean) => {
      const { perspectiveId, systemName } = group.perspective;
      if (!apiClients || !perspectiveId) return;
      const urgency = group.members.find((m) => m.subjectId === subjectId)?.urgency ?? null;
      setRemoveError(null);
      setRemovingIds((prev) => new Set(prev).add(subjectId));
      try {
        await apiClients.graphClient.delete(
          `/perspectives/${encodeURIComponent(perspectiveId)}/subjects/${encodeURIComponent(subjectId)}`
        );
        setUndo(null);
        // Re-fetch first: whether Undo is still possible is read from the refreshed `canAssign`.
        await load(true);
        if (offerUndo) {
          setUndo({ perspectiveSystemName: systemName, perspectiveLabel: label, subjectId, urgency });
        } else {
          setFocusRequest({ target: 'group', perspectiveSystemName: systemName });
        }
      } catch (err) {
        setUndo(null);
        setRemoveError({ perspective: label, reason: errorReason(err) });
      } finally {
        setRemovingIds((prev) => {
          const next = new Set(prev);
          next.delete(subjectId);
          return next;
        });
      }
    },
    [apiClients, load]
  );

  const requestRemove = useCallback(
    (action: GroupAction, subjectId: string) => {
      const member = action.group.members.find((m) => m.subjectId === subjectId);
      // Removing your own manage access can lock you out of this list, so Undo may be impossible.
      if (member?.isSelf && action.group.grantsManageAccess) {
        setSelfRemoval({ ...action, subjectId });
        return;
      }
      void performRemove(action, subjectId, true);
    },
    [performRemove]
  );

  // Cancel or Escape: nothing is sent, and focus returns to the row's Remove button.
  const cancelSelfRemoval = useCallback(() => {
    if (!selfRemoval) return;
    setFocusRequest({
      target: 'remove',
      perspectiveSystemName: selfRemoval.group.perspective.systemName,
      subjectId: selfRemoval.subjectId,
    });
    setSelfRemoval(null);
  }, [selfRemoval]);

  const runUndo = useCallback(async () => {
    if (!apiClients || !undo) return;
    setUndoing(true);
    try {
      await apiClients.graphClient.post(assignUrl, {
        perspectiveSystemName: undo.perspectiveSystemName,
        subjectId: undo.subjectId,
        ...(undo.urgency !== null ? { urgency: undo.urgency } : {}),
      });
      setUndo(null);
      await load(true);
      setFocusRequest({ target: 'row', perspectiveSystemName: undo.perspectiveSystemName, subjectId: undo.subjectId });
    } catch (err) {
      setUndo({ ...undo, failedReason: errorReason(err) });
    } finally {
      setUndoing(false);
    }
  }, [apiClients, undo, assignUrl, load]);

  useEffect(() => {
    void load();
  }, [load]);

  // Re-fetches in place, so the rest of the list stays put while the inherited block refreshes.
  const retryInherited = useCallback(async () => {
    await load(true);
    setFocusRequest({ target: 'inherited' });
  }, [load]);

  const showAll = useCallback(
    async (perspectiveSystemName: string, offset: number) => {
      if (!apiClients || !sphereId) return;
      setPaging((prev) => ({ ...prev, [perspectiveSystemName]: { loading: true, failed: false } }));
      try {
        // "Show all N" reads every remaining page, then merges them once.
        const rest: SphereMemberGroup['members'] = [];
        let last: SphereMemberGroup | undefined;
        let cursor: number | null = offset;
        while (cursor !== null) {
          const page: SphereMembersPageResponse = await apiClients.graphClient.get<SphereMembersPageResponse>(
            `/spheres/${encodeURIComponent(sphereId)}/members?perspective=${encodeURIComponent(perspectiveSystemName)}&offset=${cursor}&limit=${SHOW_ALL_PAGE_SIZE}`
          );
          last = page.groups.find((g) => g.perspective.systemName === perspectiveSystemName);
          if (!last) break;
          rest.push(...last.members);
          cursor = last.nextOffset !== null && last.nextOffset > cursor ? last.nextOffset : null;
        }
        const tail = last;
        setState((prev) =>
          prev.status !== 'ready' || !tail
            ? prev
            : {
                status: 'ready',
                data: {
                  ...prev.data,
                  groups: prev.data.groups.map((g) => {
                    if (g.perspective.systemName !== perspectiveSystemName) return g;
                    const seen = new Set(g.members.map((m) => m.subjectId));
                    return {
                      ...g,
                      members: [...g.members, ...rest.filter((m) => !seen.has(m.subjectId))],
                      total: tail.total,
                      hiddenCount: tail.hiddenCount,
                      nextOffset: null,
                    };
                  }),
                },
              }
        );
        setPaging((prev) => ({ ...prev, [perspectiveSystemName]: { loading: false, failed: false } }));
      } catch {
        setPaging((prev) => ({ ...prev, [perspectiveSystemName]: { loading: false, failed: true } }));
      }
    },
    [apiClients, sphereId]
  );

  const labelFor = useCallback(
    (group: SphereMemberGroup) => tSchema(group.perspective.labelKey, { defaultValue: group.perspective.label }),
    [tSchema]
  );

  const data = state.status === 'ready' ? state.data : null;
  const perspectiveLabels = useMemo(
    () => new Map((data?.groups ?? []).map((g) => [g.perspective.systemName, labelFor(g)])),
    [data, labelFor]
  );

  if (state.status === 'loading') {
    return (
      <output
        className="sphere-members"
        aria-busy="true"
        aria-label={t('sphere.members.loading')}
        data-testid="members-loading"
      >
        <SkeletonAtom count={4} height="2.5rem" />
      </output>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="sphere-members" data-testid="members-error">
        <p className="sphere-members__error" role="alert">
          {t(`sphere.members.error.${state.reason}`)}
        </p>
        {state.reason === 'unavailable' ? (
          <ButtonAtom
            type="button"
            variant="secondary"
            size="small"
            label={t('common.retry')}
            onClick={() => void load()}
          />
        ) : null}
      </div>
    );
  }

  const { groups, inherited, gatesUnavailable } = state.data;
  const readOnly = mode === 'edit';
  // A group with no one in it is shown only to someone who can fill it.
  const visibleGroups = groups.filter((g) => g.total > 0 || (g.canAssign && !readOnly));
  const undoGroup = undo ? groups.find((g) => g.perspective.systemName === undo.perspectiveSystemName) : undefined;

  return (
    <div className="sphere-members" data-testid="sphere-members" ref={rootRef} tabIndex={-1}>
      {readOnly ? <MessageAtom severity="info" text={t('sphere.members.editMode')} /> : null}
      {!readOnly && gatesUnavailable ? (
        <div className="sphere-members__note" data-testid="members-gates-unavailable">
          <MessageAtom severity="warn" text={t('sphere.members.gatesUnavailable')} />
          <ButtonAtom
            type="button"
            variant="text"
            size="small"
            label={t('common.retry')}
            onClick={() => void load(true)}
          />
        </div>
      ) : null}
      {!readOnly && undo ? (
        <UndoNotice
          key={undo.subjectId}
          target={undo}
          canUndo={undoGroup?.canAssign === true}
          gatesUnavailable={gatesUnavailable}
          onUndo={() => void runUndo()}
          undoing={undoing}
        />
      ) : null}
      {removeError ? (
        <p className="sphere-members__error" role="alert">
          {t('sphere.members.removeFailed', {
            perspective: removeError.perspective,
            reason: t(`sphere.members.reason.${removeError.reason}`),
          })}
        </p>
      ) : null}
      {visibleGroups.length === 0 ? (
        <EmptyStateAtom icon="pi-users" title={t('sphere.members.empty')} data-testid="members-empty" />
      ) : (
        visibleGroups.map((group) => (
          <MemberGroupMolecule
            key={group.perspective.systemName}
            group={group}
            label={labelFor(group)}
            description={tSchema(group.perspective.descriptionKey, { defaultValue: group.perspective.description })}
            readOnly={readOnly}
            removingIds={removingIds}
            loadingMore={paging[group.perspective.systemName]?.loading}
            loadMoreFailed={paging[group.perspective.systemName]?.failed}
            onAdd={() => setAddTarget({ group, label: labelFor(group) })}
            onRemove={(_name, subjectId) => requestRemove({ group, label: labelFor(group) }, subjectId)}
            onShowAll={(name, offset) => void showAll(name, offset)}
          />
        ))
      )}
      <InheritedBlock inherited={inherited} perspectiveLabels={perspectiveLabels} onRetry={() => void retryInherited()} />
      <DialogWrapper
        visible={addTarget !== null}
        header={t('sphere.members.addTo', { perspective: addTarget?.label ?? '' })}
        onHide={closeAdd}
        className="sphere-members__add-dialog"
      >
        {addTarget ? (
          <div className="sphere-members__add" data-testid="members-add-dialog">
            {addTarget.group.perspective.cardinality === 'single' ? <ReplaceNotice group={addTarget.group} /> : null}
            <span id={pickerLabelId} className="sphere-members__add-label">
              {t('sphere.members.addDialog.picker')}
            </span>
            <fieldset className="sphere-members__picker" aria-labelledby={pickerLabelId}>
              <PerspectiveSelector
                perspectiveSystemName={addTarget.group.perspective.systemName}
                sphere={sphere}
                organizationId={sphere.properties?.sphereId}
                onAssigneeSelected={(assigneeId, _sphereId, _perspective, assignee) =>
                  setSelected(assigneeId ? { id: assigneeId, name: assignee?.properties?.name ?? '' } : null)
                }
                multi={false}
                stayOpen
                lazy
              />
            </fieldset>
            {addError ? (
              <p className="sphere-members__error" role="alert">
                {t('sphere.members.addDialog.failed', {
                  name: selected?.name || t('sphere.members.unknownMember'),
                  perspective: addTarget.label,
                  reason: t(`sphere.members.reason.${addError}`),
                })}
              </p>
            ) : null}
            {selected ? null : <p className="sphere-members__note">{t('sphere.members.addDialog.choose')}</p>}
            <div className="sphere-members__add-actions">
              <ButtonAtom
                type="button"
                variant="text"
                label={t('sphere.members.addDialog.cancel')}
                onClick={closeAdd}
              />
              <ButtonAtom
                type="button"
                variant="primary"
                label={t('sphere.members.addDialog.submit')}
                disabled={!selected || adding}
                loading={adding}
                onClick={() => void submitAdd()}
              />
            </div>
          </div>
        ) : (
          <span />
        )}
      </DialogWrapper>
      <ConfirmDialogAtom
        variant="danger"
        visible={selfRemoval !== null}
        defaultFocus="reject"
        onHide={(result) => (result === 'accept' ? setSelfRemoval(null) : cancelSelfRemoval())}
        header={t('sphere.members.confirmSelf.title', { perspective: selfRemoval?.label ?? '' })}
        message={t('sphere.members.confirmSelf.body', { perspective: selfRemoval?.label ?? '' })}
        acceptLabel={t('sphere.members.confirmSelf.confirm')}
        rejectLabel={t('sphere.members.confirmSelf.cancel')}
        accept={() => {
          const pending = selfRemoval;
          setSelfRemoval(null);
          if (pending) void performRemove(pending, pending.subjectId, false);
        }}
        reject={cancelSelfRemoval}
      />
    </div>
  );
};

/**
 * Undo, the add and confirm dialogs, errors and paging all belong to one sphere: an Undo carried over
 * to another sphere would re-assign the removed person there. Keying by `sphere.id` starts each
 * sphere from a fresh state, whether or not the host remounts the slot.
 */
const SphereMembersOrganism: React.FC<SphereSectionSlotContext> = (props) => (
  <SphereMembersView key={props.sphere.id ?? ''} {...props} />
);

export default SphereMembersOrganism;
